'use strict';

import Fuse from 'fuse.js';
import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';

import { getVariantByLanguage, sortObject } from '../utils/helpers';
import useTranslation from './use_translation';

function useProduct({
	exclude_diagnostic_types = null,
	exclude_groups = null,
	exclude_markers = null,
	exclude_product_settings = null,
	exclude_product_states = null,
	exclude_product_types = null,
	exclude_products = null,
	has_groups = true,
	has_markers = false,
	illegal_words = null,
	include_diagnostic_types = null,
	include_discount = null,
	include_groups = null,
	include_markers = null,
	include_product_brands = null,
	include_product_settings = null,
	include_product_states = null,
	include_product_types = null,
	include_products = null,
	include_tag_content = null,
	include_tag_types = null,
	limit,
	maximum_markers,
	minimum_markers,
	search,
	sort,
} = {}) {
	const { language } = useTranslation('hooks_use_product');
	const data = useStaticQuery(graphql`
		query ProductHook {
			allNewProduct(
				filter: { status: { eq: "active" } }
				sort: { product_variants: { title: ASC } }
			) {
				nodes {
					default {
						group_id
					}
					diagnostics {
						type
					}
					fields {
						files_header_url_src {
							childImageSharp {
								gatsbyImageData(width: 350)
							}
						}
						product_brand_files_logo_url_src {
							childImageSharp {
								gatsbyImageData(width: 350)
							}
						}
					}
					groups
					locations
					markers
					position
					product_id
					product_brand {
						product_brand_id
						product_brand_variants {
							content
							excerpt
							language {
								code
							}
							slug
							title
						}
					}
					product_settings {
						include_biological_age
						include_infection_tracking
						include_statement
						requirement_awake_time
						requirement_daily_variation
						requirement_fasting
						requirement_medicine
						requirement_morning
						url_affiliate
					}
					product_variants {
						content
						currency {
							code
							symbol
						}
						excerpt
						language {
							code
						}
						price
						price_discount
						product_variant_id
						slug
						state
						subtitle
						title
					}
					products
					status
					tags {
						content
						type
					}
					type
				}
			}

			locales: allLocale {
				...translationFields
			}
		}
	`);

	const fuse = useMemo(() => {
		const preprocess = data?.allNewProduct?.nodes.map((item) => {
			const product_variant = getVariantByLanguage(
				language,
				item.product_variants,
			);
			if (!product_variant) return;

			return {
				content: product_variant?.content,
				excerpt: product_variant?.excerpt,
				product_id: item?.product_id,
				subtitle: product_variant?.subtitle,
				title: product_variant?.title,
			};
		});

		return new Fuse(preprocess, {
			keys: [
				'content',
				'excerpt',
				{ name: 'subtitle', weight: 2 },
				{ name: 'title', weight: 3 },
			],
			threshold: 0.4,
		});
	}, [data, language]);

	const out = useMemo(() => {
		let search_product_ids = [];
		let product_data = data.allNewProduct.nodes;
		if (fuse && search) {
			let result = fuse.search(search);
			if (result) {
				result = result.slice(0, limit ? limit : 20);
				product_data = result.map((item) => {
					return product_data.find(
						(match) => match.product_id == item.item.product_id,
					);
				});
			}
		}

		let tmp = product_data
			.map((item) => {
				if (has_groups && !item?.groups?.length) {
					return;
				} else if (has_markers && !item?.markers?.length) {
					return;
				} else if (
					exclude_groups &&
					exclude_groups.some((value) => {
						return item.groups.includes(value);
					})
				) {
					return;
				} else if (
					exclude_diagnostic_types &&
					exclude_diagnostic_types.some((value) => {
						return item.diagnostics.some(
							(diagnostic) => diagnostic.type == value,
						);
					})
				) {
					return;
				} else if (
					include_diagnostic_types &&
					!include_diagnostic_types?.some((value) => {
						return item.diagnostics?.some(
							(diagnostic) => diagnostic.type == value,
						);
					})
				) {
					return;
				} else if (
					include_groups &&
					!include_groups.some((value) => {
						return item.groups.includes(value);
					})
				) {
					return;
				} else if (
					exclude_product_types &&
					exclude_product_types.includes(item.type)
				) {
					return;
				} else if (
					include_product_brands &&
					!include_product_brands.includes(item.product_brand?.product_brand_id)
				) {
					return;
				} else if (
					include_product_types &&
					!include_product_types.includes(item.type)
				) {
					return;
				} else if (
					exclude_products &&
					exclude_products.includes(item.product_id)
				) {
					return;
				} else if (
					include_products &&
					!include_products.includes(item.product_id)
				) {
					return;
				} else if (
					exclude_product_settings?.length &&
					exclude_product_settings?.every((value) => {
						if (!item?.product_settings) return false;

						return (
							Object.hasOwn(item.product_settings, value) &&
							item.product_settings?.[value] !== undefined &&
							item.product_settings?.[value] !== null
						);
					})
				) {
					return;
				} else if (
					include_product_settings?.length &&
					!include_product_settings?.every((value) => {
						if (!item?.product_settings) return false;

						return (
							Object.hasOwn(item.product_settings, value) &&
							item.product_settings?.[value] !== undefined &&
							item.product_settings?.[value] !== null
						);
					})
				) {
					return;
				} else if (
					exclude_markers &&
					exclude_markers.some((value) => {
						return item.markers.includes(value);
					})
				) {
					return;
				} else if (
					include_markers &&
					!include_markers.some((value) => {
						return item.markers.includes(value);
					})
				) {
					return;
				} else if (maximum_markers && item?.markers?.length > maximum_markers) {
					return;
				} else if (minimum_markers && item?.markers?.length < minimum_markers) {
					return;
				} else if (
					search_product_ids?.length &&
					!search_product_ids.includes(item.product_id)
				) {
					return;
				} else if (
					include_tag_content &&
					!include_tag_content?.some((value) => {
						return item.tags?.some((tag) => tag.content == value);
					})
				) {
					return;
				} else if (
					include_tag_types &&
					!include_tag_types?.some((value) => {
						return item.tags?.some((tag) => tag.type == value);
					})
				) {
					return;
				}

				item.product_variant = getVariantByLanguage(
					language,
					item.product_variants,
				);
				if (!item.product_variant) return;
				else if (
					include_discount &&
					Number(item.product_variant.price_discount) <= 0
				) {
					return;
				} else if (
					illegal_words &&
					item.product_variant.title.toLowerCase().indexOf(illegal_words) !== -1
				) {
					return;
				} else if (
					exclude_product_states &&
					exclude_product_states.includes(item.product_variant.state)
				) {
					return;
				} else if (
					include_product_states &&
					!include_product_states.includes(item.product_variant.state)
				) {
					return;
				}

				return item;
			})
			.filter(Boolean);

		if (sort) {
			tmp = sortObject(tmp, sort);
		}

		const total = tmp.length;
		if (limit && tmp) tmp = tmp.slice(0, limit);

		return { count: { items: tmp.length, total }, items: tmp };
	}, [
		exclude_diagnostic_types,
		exclude_groups,
		exclude_markers,
		exclude_product_settings,
		exclude_product_states,
		exclude_product_types,
		exclude_products,
		fuse,
		data,
		has_groups,
		has_markers,
		illegal_words,
		include_diagnostic_types,
		include_discount,
		include_groups,
		include_markers,
		include_product_brands,
		include_product_settings,
		include_product_states,
		include_product_types,
		include_products,
		include_tag_content,
		include_tag_types,
		language,
		limit,
		maximum_markers,
		minimum_markers,
		search,
		sort,
	]);

	return out;
}

export default useProduct;
